import Kendra from 'aws-sdk/clients/kendra';
import React, { useEffect, useState } from 'react';
import db from 'just-debounce';
import {
    Autosuggest,
    AutosuggestProps,
    Form,
    Input,
    InputProps,
    NonCancelableCustomEvent,
} from '@cloudscape-design/components';
import { useTranslate } from 'src/i18n/useTranslate';
import { useApi } from 'src/hooks/useApi';
import { QuerySuggestionsMode } from '../../constants';

const DEBOUNCE_DELAY_MS = 500;
const MIN_TEXT_LENGTH_FOR_SUGGESTIONS = 2;
const MAX_TEXT_LENGTH_FOR_SUGGESTIONS = 60;

type DebounceFunction = (...args: any[]) => void;
type StatusType = 'pending' | 'loading' | 'finished' | 'error';

const debounce = (cb: DebounceFunction) =>
    // only make call at the end of every debounce delay period,
    // i.e. call at end of every 200ms if invoked
    db(cb, DEBOUNCE_DELAY_MS, false, true);

export interface SearchBarProps {
    onSubmit: (queryText: string, pageNumber: number) => void;
    initialValue?: string;
}

export const SearchBar = ({ onSubmit, initialValue }: SearchBarProps) => {
    const [queryText, setQueryText] = useState<string>(initialValue || '');
    const [suggestions, setSuggestions] = useState<Kendra.Types.SuggestionList>(
        []
    );
    const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
    const [status, setStatus] = useState<StatusType>('finished');
    const suggestionsApi = useApi({
        url: '/kendra/suggestions',
        method: 'GET',
    });
    const suggestionsConfigApi = useApi({
        url: '/kendra/suggestions-config',
        method: 'GET',
    });

    const getQuerySuggestions = async (
        queryText: string
    ): Promise<Kendra.SuggestionList | undefined> => {
        const response = await suggestionsApi.makeRequest({
            params: { queryText },
        });
        return response?.data;
    };
    const populateSuggestions = (value: string) => {
        if (!suggestionsEnabled) {
            return;
        }
        setStatus('loading');
        getQuerySuggestions(value)
            .then((result: Kendra.Types.SuggestionList | undefined) => {
                setStatus('finished');
                if (result) {
                    setSuggestions(result);
                }
            })
            .catch((err) => setStatus('error'));
    };

    const debouncedPopulateSuggestions = debounce(populateSuggestions);

    const onChange = ({ detail }: { detail: InputProps.ChangeDetail }) => {
        setQueryText(detail.value);
        if (
            suggestionsEnabled &&
            detail.value.length >= MIN_TEXT_LENGTH_FOR_SUGGESTIONS &&
            detail.value.length <= MAX_TEXT_LENGTH_FOR_SUGGESTIONS
        ) {
            debouncedPopulateSuggestions(detail.value);
        } else {
            setSuggestions([]);
        }
    };

    const onSearch = (
        event:
            | React.FormEvent<HTMLFormElement>
            | React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();

        // callback to the API call
        onSubmit(queryText, 1);
    };

    const onSelect = (
        event: NonCancelableCustomEvent<AutosuggestProps.SelectDetail>
    ) => {
        setQueryText(event.detail.value);
        onSubmit(event.detail.value, 1);
    };

    const buildSuggestionItems = () => {
        if (suggestions.length === 0) {
            return [];
        }
        return suggestions.map(buildSuggestionItemWithHighlightedLabel);
    };

    const buildSuggestionItemWithHighlightedLabel = (
        suggestion: Kendra.Types.Suggestion
    ): AutosuggestProps.Option => {
        if (suggestion && suggestion.Value && suggestion.Value.Text) {
            const suggestionText = suggestion.Value.Text.Text;
            const suggestionHighlights = suggestion.Value.Text.Highlights;
            if (suggestionText && suggestionHighlights) {
                const suggestionHighlight = suggestionHighlights[0];
                if (suggestionHighlight && suggestionHighlight.BeginOffset) {
                    const label = `${suggestionText.substring(
                        0,
                        suggestionHighlight.BeginOffset
                    )}
                                 - ${suggestionText.substring(
                                     suggestionHighlight.BeginOffset
                                 )}`;
                    return {
                        description: label,
                        value: suggestionText,
                    };
                }
            }
            return { value: suggestionText };
        }
        return { value: suggestion.Value?.Text?.Text };
    };

    const suggestionItems = buildSuggestionItems();
    const t = useTranslate();
    const placeholder = t(
        'search_placeholder_text_type_a_keyword',
        'Type a keyword to search GRU'
    );

    useEffect(() => {
        suggestionsConfigApi.makeRequest().then((response) => {
            setSuggestionsEnabled(
                response?.data
                    ? response.data.Mode === QuerySuggestionsMode.ENABLED
                    : false
            );
        });
    }, []);

    return (
        <form
            role="form"
            onSubmit={(event: React.FormEvent<HTMLFormElement>) =>
                onSearch(event)
            }
        >
            <Form>
                {suggestionsEnabled ? (
                    <Autosuggest
                        options={suggestionItems}
                        value={queryText}
                        onChange={onChange}
                        onSelect={onSelect}
                        placeholder={placeholder}
                        loadingText={t(
                            'loading_suggestions',
                            'Loading suggestions'
                        )}
                        errorText={t(
                            'error_loading_suggestions',
                            'Error loading suggestions'
                        )}
                        recoveryText={t('retry', 'Retry')}
                        finishedText={t('end_of_results', 'End of Results')}
                        statusType={status}
                        onLoadItems={() =>
                            debouncedPopulateSuggestions(queryText)
                        }
                    />
                ) : (
                    <Input
                        value={queryText}
                        onChange={onChange}
                        placeholder={placeholder}
                    />
                )}
            </Form>
        </form>
    );
};
